/**
 * @file outstanding-report.tsx
 * @module app/modules/outstanding-report/outstanding-report
 * @description Implements the outstanding report workflow for outstanding-report management.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Row,
  Form,
  Modal,
  Image,
  OverlayTrigger,
  Tooltip,
  Nav,
  Tab,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Method from "../../../utils/methods";
import { useAuth } from "../auth";
import APICallService from "../../../api/apiCallService";
import Loader from "../../../Global/loader";
import {
  multipleWarehouse,
  outStandingReport,
} from "../../../api/apiEndPoints";
import {
  listOutstandingReport,
  warehouse as warehouseStoreString,
} from "../../../utils/storeString";
import {
  OutstandingReport,
  View,
  PaymentStatusAll,
  PaymentStatusPending,
  PaymentStatusPartiallyPaid,
  PaymentStatusFullyPaid,
  ReceiptStatusAll,
  ReceiptStatusPending,
  ReceiptStatusCompleted,
  DTBBank,
  CRDBBank,
  NMBBank,
  StanbicBank,
  AirtelMoney,
  HaloPesa,
  Admin,
  OrderTigoPesa,
  OrderCash,
  OrderMPesa,
  OrderCard,
} from "../../../utils/constants";
import { IMAGES } from "../../../utils/assetsAndPermissions";
import { success, error } from "../../../Global/toast";
import { CustomSelect } from "../../custom/Select/CustomSelect";
import CustomDatePicker from "../../custom/DateRange/DatePicker";
import Pagination from "../../../Global/pagination";
import { ORDER_PAGE_LIMIT } from "../../../utils/constants";
import FsLightbox from "fslightbox-react";
import clsx from "clsx";
import blueCheck from "../../../_admin/assets/media/svg_uTrade/blue-check.svg";
import unChecked from "../../../_admin/assets/media/svg_uTrade/unchecked.svg";
import { getKey, setKey, removeKey } from "../../../Global/history";
import { useWarehouse } from "../../context/WarehouseContext";
import OrderPagination from "../../../Global/order-pagination";
import { useDebounce } from "../../../utils/useDebounce";
import { useNavigationHistory } from "../../../utils/useNavigationHistory";

const ALL_REPORT_TAB = "all";
const PENDING_REPORT_TAB = "pending";
const PENDING_RECEIPT_STATUS = 4;

const OutstandingReportComponent = () => {
  const { currentUser } = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const navigate = useNavigate();
  const { navigateTo } = useNavigationHistory();
  const [lightboxTogglers, setLightboxTogglers] = useState<{
    [key: string]: boolean;
  }>({});

  // State for data
  const [fetchLoader, setFetchLoader] = useState(true);
  const [orders, setOrders] = useState<any>([]);
  const [analytics, setAnalytics] = useState<any>({});
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  // State for pagination
  const [page, setPage] = useState(getKey(listOutstandingReport.page) || 1);
  const [pageLimit, setPageLimit] = useState(
    getKey(listOutstandingReport.limit) || ORDER_PAGE_LIMIT
  );

  // State for filters
  const [startDate, setStartDate] = useState<any>(() => {
    const savedStartDate = getKey(listOutstandingReport.filterStartDate);
    if (savedStartDate !== null && savedStartDate !== undefined) {
      try {
        const parsed = JSON.parse(savedStartDate.toString());
        return parsed ? new Date(parsed) : null;
      } catch (e) {
        return new Date();
      }
    }
    return new Date();
  });
  const [endDate, setEndDate] = useState<any>(() => {
    const savedEndDate = getKey(listOutstandingReport.filterEndDate);
    if (savedEndDate !== null && savedEndDate !== undefined) {
      try {
        const parsed = JSON.parse(savedEndDate.toString());
        return parsed ? new Date(parsed) : null;
      } catch (e) {
        return new Date();
      }
    }
    return new Date();
  });
  const [search, setSearch] = useState<string>(
    getKey(listOutstandingReport.search)?.toString() || ""
  );
  const [paymentStatus, setPaymentStatus] = useState<any>(() => {
    const saved = getKey(listOutstandingReport.filterByPaymentStatus);

    if (saved !== null && saved !== undefined) {
      try {
        const parsed = JSON.parse(saved.toString());
        return parsed ? parsed : { value: PaymentStatusAll, label: "All" };
      } catch (e) {
        return { value: PaymentStatusAll, label: "All" };
      }
    }

    return { value: PaymentStatusAll, label: "All" };
  });

  const [receiptStatus, setReceiptStatus] = useState<any>(() => {
    const saved = getKey(listOutstandingReport.filterByBankDepositStatus);

    if (saved !== null && saved !== undefined) {
      try {
        const parsed = JSON.parse(saved.toString());
        return parsed ? parsed : { value: ReceiptStatusAll, label: "All" };
      } catch (e) {
        return { value: ReceiptStatusAll, label: "All" };
      }
    }

    return { value: ReceiptStatusAll, label: "All" };
  });

  const [selectedDriver, setSelectedDriver] = useState<any>(() => {
    const saved = getKey(listOutstandingReport.filterByDriver);

    if (saved !== null && saved !== undefined) {
      try {
        const parsed = JSON.parse(saved.toString());
        return parsed ? parsed : { value: "", label: "All Drivers" };
      } catch (e) {
        return { value: "", label: "All Drivers" };
      }
    }

    return { value: "", label: "All Drivers" };
  });
  const [activeReportTab, setActiveReportTab] = useState(() => {
    const savedTab = getKey(listOutstandingReport.currentTab)?.toString();
    return savedTab === PENDING_REPORT_TAB ? PENDING_REPORT_TAB : ALL_REPORT_TAB;
  });
  const [drivers, setDrivers] = useState<any>([]);

  // Sync filters when they are updated elsewhere (e.g. PendingDepositModal)
  useEffect(() => {
    const handleFiltersUpdated = () => {
      // Payment status
      const savedPayment = getKey(listOutstandingReport.filterByPaymentStatus);
      if (savedPayment !== null && savedPayment !== undefined) {
        try {
          const parsed = JSON.parse(savedPayment.toString());
          setPaymentStatus(parsed || { value: PaymentStatusAll, label: "All" });
        } catch (e) {
          setPaymentStatus({ value: PaymentStatusAll, label: "All" });
        }
      } else {
        setPaymentStatus({ value: PaymentStatusAll, label: "All" });
      }

      // Receipt/bank deposit status
      const savedReceipt = getKey(
        listOutstandingReport.filterByBankDepositStatus
      );
      if (savedReceipt !== null && savedReceipt !== undefined) {
        try {
          const parsed = JSON.parse(savedReceipt.toString());
          setReceiptStatus(parsed || { value: ReceiptStatusAll, label: "All" });
        } catch (e) {
          setReceiptStatus({ value: ReceiptStatusAll, label: "All" });
        }
      } else {
        setReceiptStatus({ value: ReceiptStatusAll, label: "All" });
      }

      // Driver filter
      const savedDriver = getKey(listOutstandingReport.filterByDriver);
      if (savedDriver !== null && savedDriver !== undefined) {
        try {
          const parsed = JSON.parse(savedDriver.toString());
          setSelectedDriver(parsed || { value: "", label: "All Drivers" });
        } catch (e) {
          setSelectedDriver({ value: "", label: "All Drivers" });
        }
      } else {
        setSelectedDriver({ value: "", label: "All Drivers" });
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener(
        "outstanding-filters-updated",
        handleFiltersUpdated
      );
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener(
          "outstanding-filters-updated",
          handleFiltersUpdated
        );
      }
    };
  }, []);

  // State for receipt modal
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState<any>(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [verified, setVerified] = useState(false);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    string | undefined
  >(undefined);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [totalAmount, setTotalAmount] = useState([]);

  // State for bank selection modal
  const [showBankModal, setShowBankModal] = useState(false);
  const [selectedBank, setSelectedBank] = useState(DTBBank);
  const [transactionId, setTransactionId] = useState("");
  const [transactionValidation, setTransactionValidation] = useState(false);
  const [verifyingOrder, setVerifyingOrder] = useState<any>(null);
  const [verifyingTransactionId, setVerifyingTransactionId] =
    useState<any>(null);
  const [showTigoModal, setShowTigoModal] = useState(false);
  const [tigoReferenceNo, setTigoReferenceNo] = useState("");
  const [tigoReferenceValidation, setTigoReferenceValidation] =
    useState(false);
  const [tigoReceiptFile, setTigoReceiptFile] = useState<any>(null);
  const [tigoReceiptValidation, setTigoReceiptValidation] = useState(false);
  const [tigoSubmitting, setTigoSubmitting] = useState(false);

  // Payment status options
  const paymentStatusOptions = [
    { value: PaymentStatusAll, label: "All", title: "All" },
    { value: PaymentStatusPending, label: "Pending", title: "Pending" },
    {
      value: PaymentStatusPartiallyPaid,
      label: "Partially Paid",
      title: "Partially Paid",
    },
    { value: PaymentStatusFullyPaid, label: "Fully Paid", title: "Fully Paid" },
  ];

  // Receipt status options
  const receiptStatusOptions = [
    { value: ReceiptStatusAll, label: "All", title: "All" },
    { value: ReceiptStatusPending, label: "Pending", title: "Pending" },
    { value: ReceiptStatusCompleted, label: "Completed", title: "Completed" },
  ];

  const effectiveReceiptStatus =
    activeReportTab === PENDING_REPORT_TAB
      ? PENDING_RECEIPT_STATUS
      : receiptStatus.value;

  const fetchDetails = async () => {
    setFetchLoader(true);

    setLoading(true);
    let params: any = {
      pageNo: page,
      limit: pageLimit,
      sortKey: "_createdAt",
      sortOrder: -1,
      needCount: true,
    };

    // Add filters - always pass current date as default
    params.fromDate = Method.convertDateToFormat(startDate, "YYYY-MM-DD");
    params.toDate = Method.convertDateToFormat(endDate, "YYYY-MM-DD");
    if (search) {
      params.searchTerm = search;
    }
    if (paymentStatus.value !== PaymentStatusAll) {
      params.paymentStatus = paymentStatus.value;
    }
    if (effectiveReceiptStatus !== ReceiptStatusAll) {
      params.receiptStatus = effectiveReceiptStatus;
    }
    if (selectedDriver.value) {
      params.driver = selectedDriver.value;
    }
    if (selectedWarehouse?.value || selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value || selectedWarehouse?.value;
    }
    if (paymentStatus.value == PaymentStatusPartiallyPaid) {
      params.listType = 4;
    }
    let endpoint = outStandingReport.list;
    let apiService = new APICallService(
      endpoint,
      params,
      "",
      "",
      "",
      "",
      OutstandingReport,
      selectedWarehouse?.value || selectedWarehouse?.value || null
    );

    try {
      let response: any = await apiService.callAPI();
      if (response.records) {
        setTotalRecords(response.total);
        setOrders(response.records);
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
    }
    setFetchLoader(false);
    setLoading(false);
  };
  const fetchTotal = async (
    startDate: string,
    endDate: string,
    driver: string
  ) => {
    let params: any = {
      fromDate: startDate
        ? Method.convertDateToFormat(startDate, "YYYY-MM-DD")
        : "",
      toDate: endDate ? Method.convertDateToFormat(endDate, "YYYY-MM-DD") : "",
    };
    if (selectedDriver.value) {
      params.driver = selectedDriver.value;
    }
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value || null;
    }

    let apiService = new APICallService(
      outStandingReport.totalPayments,
      params,
      "",
      "",
      "",
      "",
      OutstandingReport,
      selectedWarehouse?.value || null
    );
    let response = await apiService.callAPI();
    if (response) {
      setTotalAmount(response);
    } else {
      setTotalAmount([]);
    }
  };
  const fetchAnalytics = async () => {
    let params: any = {};
    setAnalytics([]);

    // Always pass current date as default
    params.fromDate = Method.convertDateToFormat(startDate, "YYYY-MM-DD");
    params.toDate = Method.convertDateToFormat(endDate, "YYYY-MM-DD");
    if (selectedDriver.value) {
      params.driver = selectedDriver.value;
    }
    if (selectedWarehouse?.value || selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value || selectedWarehouse?.value;
    }

    let endpoint = outStandingReport.analytics;
    let apiService = new APICallService(
      endpoint,
      params,
      "",
      "",
      "",
      "",
      OutstandingReport,
      selectedWarehouse?.value || selectedWarehouse?.value || null
    );

    try {
      let response: any = await apiService.callAPI();
      if (response && response.length > 0) {
        setAnalytics(response[0]);
      } else {
      }
    } catch (err) {
      console.error("Error fetching analytics:", err);
    }
  };

  const fetchDrivers = async () => {
    let params: any = {};

    // Always pass current date as default
    params.fromDate = Method.convertDateToFormat(startDate, "YYYY-MM-DD");
    params.toDate = Method.convertDateToFormat(endDate, "YYYY-MM-DD");
    if (selectedWarehouse?.value || selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value || selectedWarehouse?.value;
    }

    let endpoint = outStandingReport.listDrivers;
    let apiService = new APICallService(
      endpoint,
      params,
      "",
      "",
      "",
      "",
      OutstandingReport,
      selectedWarehouse?.value || selectedWarehouse?.value || null
    );

    try {
      let response: any = await apiService.callAPI();
      if (response && response.length > 0) {
        // Transform the response to match the expected format
        const driverOptions = response.map((driver: any) => ({
          value: driver.reference,
          label: driver.name,
          title: driver.name,
        }));
        setDrivers(driverOptions);
      }
    } catch (err) {
      console.error("Error fetching drivers:", err);
    }
  };

  useEffect(() => {
    if (!Method.hasPermission(OutstandingReport, View, currentUser)) {
      return window.history.back();
    }
    if (startDate && endDate) {
      fetchDetails();
      fetchAnalytics();
      fetchDrivers();
      fetchTotal(startDate, endDate, selectedDriver);
    }
  }, [
    page,
    pageLimit,
    startDate,
    endDate,
    paymentStatus,
    receiptStatus,
    activeReportTab,
    selectedDriver,
    selectedWarehouse,
    search,
  ]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageLimitChange = (event: any) => {
    setPageLimit(parseInt(event.target.value));
    setPage(1);
  };

  const handleDateFilter = ([start, end]: any) => {
    setStartDate(start);
    setEndDate(end);
    if (start && end) {
      setPage(1);
      setKey(listOutstandingReport.filterStartDate, start);
      setKey(listOutstandingReport.filterEndDate, end);
      setKey(listOutstandingReport.page, 1);
    }
  };
  const handleSearch = (value: string) => {
    setSearch(value);
    setKey(listOutstandingReport.search, value, false);
    setPage(1);
    setKey(listOutstandingReport.page, 1);
  };

  const handlePaymentStatusChange = (selected: any) => {
    setPaymentStatus(selected);
    setPage(1);
    setKey(listOutstandingReport.filterByPaymentStatus, selected);
    setKey(listOutstandingReport.page, 1);
  };

  const handleReceiptStatusChange = (selected: any) => {
    setReceiptStatus(selected);
    setPage(1);
    setKey(listOutstandingReport.filterByBankDepositStatus, selected);
    setKey(listOutstandingReport.page, 1);
  };

  const handleDriverChange = (selected: any) => {
    setSelectedDriver(selected);
    setPage(1);
    setKey(listOutstandingReport.filterByDriver, selected);
    setKey(listOutstandingReport.page, 1);
  };

  const handleReportTabChange = (tab: string) => {
    setActiveReportTab(tab);
    setPage(1);
    setKey(listOutstandingReport.currentTab, tab);
    setKey(listOutstandingReport.page, 1);

    // When switching to pending tab, set Bank Deposit Status filter to "Pending"
    if (tab === PENDING_REPORT_TAB) {
      const pendingOption = {
        value: ReceiptStatusPending,
        label: "Pending",
        title: "Pending",
      };
      setReceiptStatus(pendingOption);
      setKey(listOutstandingReport.filterByBankDepositStatus, pendingOption);
    }
  };

  const kgToTons = (kilograms: number) => {
    // Missing or non-numeric weight shows '-' instead of NaN
    if (!Number.isFinite(Number(kilograms))) return '-';
    const conversionFactor = 1000;
    const tons = kilograms / conversionFactor;
    return tons.toFixed(3);
  };

  const toggleOrderExpansion = (orderId: string) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  const handleUploadReceipt = (order: any, transactionId?: string) => {
    setSelectedOrder(order);
    setSelectedTransactionId(transactionId);
    setSelectedFile(null);
    setShowReceiptModal(true);
  };

  const handleViewReceipt = (receipt: any, transactionId?: string) => {
    setSelectedReceipt(receipt);
    setSelectedTransactionId(transactionId);
    setShowReceiptModal(true);
  };

  const handleFileChange = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile || !selectedOrder) {
      error("Please select a file to upload");
      return;
    }

    setUploadingReceipt(true);

    try {
      const formData = new FormData();
      formData.append("image", selectedFile);

      // Use the selected transaction ID or generate a new one
      let endpoint = outStandingReport.paymentReceipt;
      let apiService = new APICallService(
        endpoint,
        formData,
        {
          orderId: selectedOrder._id,
          transactionId: selectedTransactionId,
        },
        "",
        "",
        "",
        OutstandingReport,
        selectedWarehouse?.value || selectedWarehouse?.value || null
      );

      let response = await apiService.callAPI();
      if (response) {
        success("Receipt uploaded successfully");
        setShowReceiptModal(false);
        setSelectedTransactionId(undefined);
        fetchDetails(); // Refresh the list
        fetchAnalytics(); // Refresh analytics
      }
    } catch (err) {
      error("Failed to upload receipt");
      console.error("Error uploading receipt:", err);
    }

    setUploadingReceipt(false);
  };

  const handleVerified = async (order: any, transactionId: any) => {
    setVerifyingOrder(order);
    setVerifyingTransactionId(transactionId);
    setShowBankModal(true);
  };

  const handleTigoVerification = async (order: any, transactionId: any) => {
    setSelectedOrder(order);
    setSelectedTransactionId(transactionId);
    setTigoReferenceNo("");
    setTigoReceiptFile(null);
    setTigoReferenceValidation(false);
    setTigoReceiptValidation(false);
    setShowTigoModal(true);
  };

  const handleBankSelection = async (bank: any) => {
    setSelectedBank(bank);
    if (transactionId == "") {
      setTransactionValidation(true);
      return;
    }
    try {
      // Use the selected transaction ID or generate a new one
      let endpoint = outStandingReport.paymentReceiptStatus;
      let apiService = new APICallService(
        endpoint,
        {
          transactionId: transactionId,
          settledPaymentMethod: bank, // Pass the selected bank as payment mode
        },
        {
          orderId: verifyingOrder._id,
          transactionId: verifyingTransactionId,
        },
        "",
        "",
        "",
        OutstandingReport,
        selectedWarehouse?.value || selectedWarehouse?.value || null
      );

      let response = await apiService.callAPI();
      if (response) {
        success("Receipt verified successfully");
        fetchDetails();
        setVerified(true);
      }
    } catch (err) {
      error("Failed to verify receipt");
      console.error("Error verifying receipt:", err);
    }
    setShowBankModal(false);
    setTransactionValidation(false);

    setVerified(false);
    setVerifyingOrder(null);
    setVerifyingTransactionId(null);
  };

  const handleSkipVerification = async (order: any, transactionId: any) => {
    setVerified(true);

    try {
      let endpoint = outStandingReport.paymentSkipped;
      let apiService = new APICallService(
        endpoint,
        {},
        {
          orderId: order._id,
          transactionId: transactionId,
        },
        "",
        "",
        "",
        OutstandingReport,
        selectedWarehouse?.value || selectedWarehouse?.value || null
      );

      let response = await apiService.callAPI();
      if (response) {
        success("Receipt skipped successfully");
        fetchDetails();
      }
    } catch (err) {
      error("Failed to skip receipt");
      console.error("Error skipping receipt:", err);
    }

    setVerified(false);
  };

  const handleTigoReceiptChange = (event: any) => {
    const file = event.target.files[0];
    setTigoReceiptFile(file || null);
    setTigoReceiptValidation(!file);
  };

  const handleTigoSubmit = async () => {
    const trimmedReferenceNo = tigoReferenceNo.trim();

    setTigoReferenceValidation(!trimmedReferenceNo);
    setTigoReceiptValidation(!tigoReceiptFile);

    if (!trimmedReferenceNo || !tigoReceiptFile || !selectedOrder) {
      return;
    }

    setTigoSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("tigoReferenceNo", trimmedReferenceNo);
      formData.append("image", tigoReceiptFile);

      const apiService = new APICallService(
        outStandingReport.tigoPesaVerification,
        formData,
        {
          orderId: selectedOrder._id,
          transactionId: selectedTransactionId,
        },
        "",
        "",
        "",
        OutstandingReport,
        selectedWarehouse?.value || selectedWarehouse?.value || null
      );

      const response = await apiService.callAPI();
      if (response) {
        success("Tigo Pesa verified successfully");
        setShowTigoModal(false);
        setSelectedTransactionId(undefined);
        setTigoReferenceNo("");
        setTigoReceiptFile(null);
        fetchDetails();
      }
    } catch (err) {
      error("Failed to verify Tigo Pesa payment");
      console.error("Error verifying Tigo Pesa payment:", err);
    }

    setTigoSubmitting(false);
  };

  const toggleLightbox = (transactionId: string) => {
    setLightboxTogglers((prev) => ({
      ...prev,
      [transactionId]: !prev[transactionId],
    }));
  };

  const handleCurrentPage = async (val: number) => {
    setLoading(true);
    setPage(val);
    setKey(listOutstandingReport.page, val);
    await fetchDetails();
    setLoading(false);
  };
  const handleNextPage = async (val: number) => {
    setLoading(true);
    setPage(val + 1);
    setKey(listOutstandingReport.page, val + 1);
    await fetchDetails();
    setLoading(false);
  };
  const handlePreviousPage = async (val: number) => {
    setLoading(true);
    setPage(val - 1);
    setKey(listOutstandingReport.page, val - 1);
    await fetchDetails();
    setLoading(false);
  };
  const handlePageLimit = async (event: any) => {
    setLoading(true);
    setPage(1);
    setPageLimit(parseInt(event.target.value));
    setKey(listOutstandingReport.page, 1);
    setKey(listOutstandingReport.limit, parseInt(event.target.value));
    await fetchDetails();
    setLoading(false);
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case OrderCash.toString():
        return "Cash";
      case OrderTigoPesa.toString():
        return "Tigo Pesa";
      case OrderMPesa.toString():
        return "MPesa";
      case OrderCard.toString():
        return "Card";
      case DTBBank.toString():
        return "DTB Bank";
      case CRDBBank.toString():
        return "CRDB Bank";
      case NMBBank.toString():
        return "NMB Bank";
      case StanbicBank.toString():
        return "Stanbic Bank";
      case AirtelMoney.toString():
        return "Airtel Money";
      case HaloPesa.toString():
        return "Halo Pesa";
      default:
        return "";
    }
  };

  const isTigoTransaction = (transaction: any) => {
    return transaction?.paymentMethod?.toString() === OrderTigoPesa.toString();
  };

  const isCashTransaction = (transaction: any) => {
    return transaction?.paymentMethod?.toString() === OrderCash.toString();
  };

  const isTigoVerified = (transaction: any) => {
    return Boolean(transaction?.tigoReceipt && transaction?.tigoReferenceNo);
  };

  const isVerificationResolved = (transaction: any) => {
    if (isCashTransaction(transaction)) {
      return Boolean(transaction?.skipped || transaction?.verified);
    }

    if (isTigoTransaction(transaction)) {
      return isTigoVerified(transaction);
    }

    return true;
  };

  const hasPendingVerification = (order: any) => {
    return Boolean(
      order?.payment?.transactions?.some((transaction: any) => {
        const isCashOrTigo =
          isCashTransaction(transaction) || isTigoTransaction(transaction);

        return isCashOrTigo && !isVerificationResolved(transaction);
      })
    );
  };
  return (
    <>
      <div className="p-5">
        <Row className="align-items-center g-md-5 g-3 mb-7">
          <Col xs>
            <h1 className="fs-22 fw-bolder">Outstanding Report</h1>
          </Col>
          <Col xs="auto">
            {/* <CustomDatePicker
              className={clsx(
                "form-control bg-white min-h-60px fs-16 fw-bold ",
                !endDate ? "border-danger" : ""
              )}
              onChange={handleDateFilter}
              selected={endDate}
              selectsRange={false}
              startDate={endDate}
              dateFormat="dd/MM/yyyy"
              showFullMonthYearPicker={true}
              maxDate={new Date()}
              inputTextBG="bg-white"
            /> */}
            <CustomDatePicker
              className="form-control bg-white min-h-30px fs-16 fw-bold text-dark min-w-md-288px min-w-175px"
              onChange={handleDateFilter}
              selectsRange={true}
              startDate={startDate}
              endDate={endDate}
              dateFormat="dd/MM/yyyy"
              showFullMonthYearPicker={true}
              maxDate={new Date()}
              inputTextBG="bg-white"
            />
          </Col>
        </Row>

        {/* Analytics Cards */}
        <Row className="g-5 mb-7">
          <Col xl={4} md={6} sm={4}>
            <div className="border-r8px bg-primary rounded py-4 px-5">
              <div className="d-flex justify-content-between">
                <div className="d-flex flex-column">
                  <span className="fs-22 fw-bolder text-white">
                    {/* {analytics.totalOngoingOrders || 0} */}
                    {totalRecords || 0}
                  </span>
                  <span className="fw-500 fs-16 text-white">Total Orders</span>
                </div>
                <img src={IMAGES.OrderSVG} alt="" />
              </div>
            </div>
          </Col>

          <Col xl={4} md={6} sm={4}>
            <div className="border-r8px bg-warning rounded py-4 px-5">
              <div className="d-flex justify-content-between">
                <div className="d-flex flex-column">
                  <span className="fs-22 fw-bolder text-white">
                    TSh{" "}
                    {Method.getGeneralizedAmount(
                      analytics.totalCashDeposited || 0
                    )}
                  </span>
                  <span className="fw-500 fs-16 text-white">
                    Total Cash Deposited
                  </span>
                </div>
                <img src={IMAGES.PaymentSVG} alt="" />
              </div>
            </div>
          </Col>
          <Col xl={4} md={6} sm={4}>
            <div className="border-r8px bg-danger rounded py-4 px-5">
              <div className="d-flex justify-content-between">
                <div className="d-flex flex-column">
                  <span className="fs-22 fw-bolder text-white">
                    TSh{" "}
                    {Method.getGeneralizedAmount(
                      analytics.pendingCashDeposit || 0
                    )}
                  </span>
                  <span className="fw-500 fs-16 text-white">
                    Pending Cash Deposit
                  </span>
                </div>
                <img src={IMAGES.YellowError} alt="" />
              </div>
            </div>
          </Col>
        </Row>
        <Row className="align-items-center mb-7 g-6">
          {totalAmount &&
            totalAmount.length > 0 &&
            totalAmount.map((val: any, index: number) => (
              <Col xl={4} md={6} sm={4} key={index}>
                <div className="border-r8px bg-fbeace rounded py-4 px-5">
                  <div className="d-flex justify-content-between">
                    <div className="d-flex flex-column">
                      <span className="fs-18 fw-bolder">
                        {val.charge > 0
                          ? "TSh " + Method.getGeneralizedAmount(val.charge)
                          : 0}
                      </span>
                      <span className="fw-medium fs-16">
                        {getTypeLabel(val.paymentMethod.toString())}
                      </span>
                    </div>
                  </div>
                </div>
              </Col>
            ))}
        </Row>
        <div className="custom-tabContainer">
          <Tab.Container activeKey={activeReportTab}>
            <Row className="variant-categories">
              <Col lg={"auto"} className="mb-6">
                <div className="bg-light border border-r8px p-3 px-md-6">
                  <Nav variant="pills">
                    <Nav.Item>
                      <Nav.Link
                        eventKey={ALL_REPORT_TAB}
                        onClick={() => handleReportTabChange(ALL_REPORT_TAB)}
                        active={activeReportTab === ALL_REPORT_TAB}
                      >
                        All
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey={PENDING_REPORT_TAB}
                        onClick={() => handleReportTabChange(PENDING_REPORT_TAB)}
                        active={activeReportTab === PENDING_REPORT_TAB}
                      >
                        Pending
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                </div>
              </Col>
            </Row>
          </Tab.Container>
        </div>
        {/* Filters */}
        <Card className="border border-r10px mb-7">
          <Card.Body className="p-6">
            <Row className="g-4">
              <Col lg={3} md={6}>
                <Form.Label className="fs-16 fw-600 text-dark mb-3">
                  Payment Status
                </Form.Label>
                <CustomSelect
                  value={paymentStatus}
                  onChange={handlePaymentStatusChange}
                  options={paymentStatusOptions}
                  placeholder="Select payment status"
                  minHeight="60px"
                />
              </Col>
              {activeReportTab !== PENDING_REPORT_TAB && (
                <Col lg={3} md={6}>
                  <Form.Label className="fs-16 fw-600 text-dark mb-3">
                    Bank Deposit Status
                  </Form.Label>
                  <CustomSelect
                    value={receiptStatus}
                    onChange={handleReceiptStatusChange}
                    options={receiptStatusOptions}
                    placeholder="Select receipt status"
                    minHeight="60px"
                  />
                </Col>
              )}
              <Col lg={3} md={6}>
                <Form.Label className="fs-16 fw-600 text-dark mb-3">
                  Driver
                </Form.Label>
                <CustomSelect
                  value={selectedDriver}
                  onChange={handleDriverChange}
                  options={[
                    { value: "", label: "All Drivers", title: "All Drivers" },
                    ...drivers,
                  ]}
                  placeholder="Select driver"
                  minHeight="60px"
                />
              </Col>
              <Col lg={3} md={6}>
                <Form.Label className="fs-16 fw-600 text-dark mb-3">
                  Search
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search by order ID"
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="form-control-lg"
                  style={{ height: "60px" }}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Orders Table */}
        <Row>
          <Col xs={12}>
            {!fetchLoader ? (
              orders && orders?.length ? (
                <>
                  <Card className="border border-r10px">
                    <Card.Body className="p-0">
                      <div className="table-responsive">
                        <table className="table table-rounded table-row-bordered align-middle gs-7 gy-6 mb-0">
                          <thead>
                            <tr className="fw-bold fs-16 fw-600 text-dark border-bottom align-middle">
                              <th className="min-w-md-175px min-w-150px">
                                Order Date & ID
                              </th>
                              <th className="min-w-md-200px min-w-125px">
                                Customer Name
                              </th>
                              <th className="min-w-md-125px">Quantity</th>
                              <th className="min-w-md-125px">Weight</th>
                              <th className="min-w-md-125px min-w-100px">
                                Total Amount
                              </th>
                              <th className="min-w-md-125px min-w-100px">
                                Remaining Amount
                              </th>
                              <th className="min-w-100px text-center">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {orders.map((order: any, index: number) => {
                              const isExpanded = expandedOrders.has(order._id);
                              const hasTransactions =
                                order.payment?.transactions?.length > 0;
                              const showPendingVerificationDot =
                                hasPendingVerification(order);

                              return (
                                <React.Fragment key={index}>
                                  {/* Main Order Row */}
                                  <tr>
                                    <td className="fs-15 fw-500">
                                      <div className="d-flex align-items-center">
                                        <div className="d-flex align-items-start flex-column">
                                          <span className="text-dark d-block">
                                            {Method.convertDateToDDMMYYYYHOURS(
                                              order._createdAt
                                            )}
                                          </span>
                                          <span className="text-gray d-block">
                                            {order.refKey}
                                          </span>
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <span className="fs-15 fw-500 d-block">
                                        {order.customer.name}
                                      </span>
                                    </td>
                                    <td>
                                      <span className="fs-15 fw-500">
                                        {order.orderedQuantities} items
                                      </span>
                                    </td>
                                    <td>
                                      <span className="fs-15 fw-500">
                                        {kgToTons(
                                          order.totalQuantities.reduce(
                                            (total: number, item: any) =>
                                              total + (Number(item?.weight) || 0),
                                            0
                                          )
                                        )}{" "}
                                        tons
                                      </span>
                                    </td>
                                    <td>
                                      <span className="fs-15 fw-500">
                                        TSh{" "}
                                        {Method.getGeneralizedAmount(
                                          order.payment.totalCharge
                                        )}
                                      </span>
                                    </td>
                                    <td>
                                      <span className="fs-15 fw-500">
                                        TSh{" "}
                                        {Method.getGeneralizedAmount(
                                          order.payment.remainingCharge
                                        )}
                                      </span>
                                    </td>
                                    <td className="text-end">
                                      <div className="d-flex gap-2 justify-content-end">
                                        <Button className="uw-btn uw-btn-light uw-btn-sm"
                                          variant="outline-secondary"
                                          size="sm"
                                          onClick={() => {
                                            navigateTo(
                                              "/orders/order-details",
                                              {
                                                state: {
                                                  id: order._id,
                                                  moduleName: OutstandingReport,
                                                },
                                              }
                                            );
                                          }}
                                        >
                                          View Details
                                        </Button>
                                        {showPendingVerificationDot && (
                                          <span
                                            className="bg-danger rounded-circle align-self-center"
                                            style={{ width: "10px", height: "10px", marginRight: "1rem" }}
                                            title="Verification action pending"
                                          ></span>
                                        )}
                                        {hasTransactions && (
                                          <Button
                                            variant="link"
                                            className="p-0 me-2 text-muted"
                                            onClick={() =>
                                              toggleOrderExpansion(order._id)
                                            }
                                          >
                                            <i
                                              className={`fas fa-chevron-${
                                                isExpanded ? "up" : "down"
                                              }`}
                                            ></i>
                                          </Button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>

                                  {/* Transaction Details Row - Only show when expanded */}
                                  {isExpanded && hasTransactions && (
                                    <tr className="bg-light">
                                      <td colSpan={11}>
                                        <table className="table table-row-bordered border border-r10px datatable align-middle gs-7 gy-4 my-0 bg-white">
                                          <thead>
                                            <tr className="fs-16 fw-600 h-65px align-middle">
                                              <th className="">Date</th>
                                              <th className="">Amount</th>
                                              <th className="">Payment Mode</th>
                                              <th className="">
                                                Bank Deposit Status
                                              </th>
                                              <th className="">
                                                Verification Status
                                              </th>
                                              <th className="text-center">
                                                Actions
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody className="fs-15 fw-600">
                                            <>
                                              {order.payment.transactions
                                                .length ? (
                                                <>
                                                  {order.payment.transactions.map(
                                                    (
                                                      transaction: any,
                                                      transIndex: number
                                                    ) => {
                                                      const isTigoPayment =
                                                        isTigoTransaction(
                                                          transaction
                                                        );
                                                      const tigoVerified =
                                                        isTigoVerified(
                                                          transaction
                                                        );
                                                      const receiptUrl =
                                                        isTigoPayment
                                                          ? transaction.tigoReceipt
                                                          : transaction.receipt;

                                                      return (
                                                      <React.Fragment
                                                        key={index}
                                                      >
                                                        <tr>
                                                          <td>
                                                            <span className="fs-15 fw-600">
                                                              {Method.convertDateToFormat(
                                                                transaction.completedAt,
                                                                "DD/MM/YYYY"
                                                              )}
                                                            </span>
                                                          </td>
                                                          <td>
                                                            <span className="fs-15 fw-600">
                                                              TSh{" "}
                                                              {Method.getGeneralizedAmount(
                                                                transaction.charge ||
                                                                  0
                                                              )}
                                                            </span>
                                                          </td>

                                                          <td>
                                                            {transaction.paymentMethod ===
                                                            1
                                                              ? "Paid via Cash"
                                                              : "Paid via Tigo Pesa"}
                                                          </td>
                                                          <td>
                                                            {transaction.receipt ? (
                                                              <span className="badge badge-success fs-14">
                                                                Completed {transaction.transactionId ? `(${transaction.transactionId})` : ""}
                                                              </span>
                                                            ) : (
                                                              <>
                                                                <span className="badge badge-warning fs-14">
                                                                  Pending
                                                                </span>
                                                              </>
                                                            )}
                                                          </td>
                                                          <td>
                                                            {transaction.skipped ||
                                                            (!isTigoPayment &&
                                                              transaction.receipt &&
                                                              transaction.verified) ||
                                                            (isTigoPayment &&
                                                              tigoVerified) ? (
                                                              <>
                                                                {transaction.skipped ? (
                                                                  <span className="badge badge-warning fs-14">
                                                                    Skipped
                                                                    Verification
                                                                  </span>
                                                                ) : (
                                                                  <span className="badge badge-success fs-14">
                                                                    Verified
                                                                  </span>
                                                                )}
                                                              </>
                                                            ) : (
                                                              <>
                                                                {isTigoPayment ? (
                                                                  <div className="d-flex gap-2">
                                                                    <Button className="uw-btn uw-btn-primary uw-btn-sm"
                                                                      variant="primary"
                                                                      size="sm"
                                                                      onClick={() =>
                                                                        handleTigoVerification(
                                                                          order,
                                                                          transaction._id
                                                                        )
                                                                      }
                                                                    >
                                                                      <span className="indicator-label fs-16 fw-bolder">
                                                                        Verify
                                                                      </span>
                                                                    </Button>
                                                                  </div>
                                                                ) : transaction.receipt ? (
                                                                  <>
                                                                    {transaction.verified ? (
                                                                      <span className="badge badge-primary fs-14">
                                                                        Verified
                                                                      </span>
                                                                    ) : (
                                                                      <>
                                                                        <div className="d-flex gap-2">
                                                                          <Button className="uw-btn uw-btn-primary uw-btn-sm"
                                                                            variant="primary"
                                                                            size="sm"
                                                                            onClick={() =>
                                                                              handleVerified(
                                                                                order,
                                                                                transaction._id
                                                                              )
                                                                            }
                                                                          >
                                                                            {loading ? (
                                                                              <span
                                                                                className="indicator-progress"
                                                                                style={{
                                                                                  display:
                                                                                    "block",
                                                                                }}
                                                                              >
                                                                                Please
                                                                                wait...
                                                                                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                                                              </span>
                                                                            ) : (
                                                                              <span className="indicator-label fs-16 fw-bolder">
                                                                                Verify
                                                                              </span>
                                                                            )}
                                                                          </Button>
                                                                        </div>
                                                                      </>
                                                                    )}
                                                                  </>
                                                                ) : (
                                                                  <>
                                                                    <span className="badge badge-grey fs-14 text-dark">
                                                                      Deposit
                                                                      Pending
                                                                    </span>
                                                                    {currentUser?.userType ==
                                                                    Admin ? (
                                                                      <Button className="uw-btn uw-btn-light uw-btn-sm"
                                                                        variant="outline-secondary"
                                                                        size="sm"
                                                                        onClick={() =>
                                                                          handleSkipVerification(
                                                                            order,
                                                                            transaction._id
                                                                          )
                                                                        }
                                                                        disabled={
                                                                          verified
                                                                        }
                                                                      >
                                                                        {verified ? (
                                                                          <span
                                                                            className="indicator-progress"
                                                                            style={{
                                                                              display:
                                                                                "block",
                                                                            }}
                                                                          >
                                                                            Please
                                                                            wait...
                                                                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                                                          </span>
                                                                        ) : (
                                                                          <span className="indicator-label fs-16 fw-bolder">
                                                                            Skip
                                                                          </span>
                                                                        )}
                                                                      </Button>
                                                                    ) : (
                                                                      <>
                                                                        <Button className="uw-btn uw-btn-light uw-btn-sm"
                                                                          variant="outline-secondary"
                                                                          size="sm"
                                                                          onClick={() =>
                                                                            handleSkipVerification(
                                                                              order,
                                                                              transaction._id
                                                                            )
                                                                          }
                                                                          disabled={
                                                                            verified
                                                                          }
                                                                        >
                                                                          {verified ? (
                                                                            <span
                                                                              className="indicator-progress"
                                                                              style={{
                                                                                display:
                                                                                  "block",
                                                                              }}
                                                                            >
                                                                              Please
                                                                              wait...
                                                                              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                                                            </span>
                                                                          ) : (
                                                                            <span className="indicator-label fs-16 fw-bolder">
                                                                              Skip
                                                                            </span>
                                                                          )}
                                                                        </Button>
                                                                      </>
                                                                    )}
                                                                  </>
                                                                )}
                                                              </>
                                                            )}
                                                          </td>
                                                          <td>
                                                            <div className="d-flex gap-2 justify-content-center">
                                                              {receiptUrl ? (
                                                                <>
                                                                  <FsLightbox
                                                                    toggler={
                                                                      lightboxTogglers[
                                                                        transaction
                                                                          ._id
                                                                      ] || false
                                                                    }
                                                                    disableLocalStorage={
                                                                      true
                                                                    }
                                                                    sources={[
                                                                      receiptUrl,
                                                                    ]}
                                                                    exitFullscreenOnClose={
                                                                      true
                                                                    }
                                                                  />
                                                                  <Button className="uw-btn uw-btn-primary uw-btn-sm"
                                                                    variant="primary"
                                                                    size="sm"
                                                                    onClick={() =>
                                                                      toggleLightbox(
                                                                        transaction._id
                                                                      )
                                                                    }
                                                                  >
                                                                    View
                                                                    Receipt
                                                                  </Button>
                                                                </>
                                                              ) : !isTigoPayment ? (
                                                                <>
                                                                  <Button className="uw-btn uw-btn-primary uw-btn-sm"
                                                                    variant="primary"
                                                                    size="sm"
                                                                    onClick={() =>
                                                                      handleUploadReceipt(
                                                                        order,
                                                                        transaction._id
                                                                      )
                                                                    }
                                                                  >
                                                                    Upload
                                                                    Receipt
                                                                  </Button>
                                                                </>
                                                              ) : (
                                                                <OverlayTrigger
                                                                  placement="top"
                                                                  overlay={
                                                                    <Tooltip
                                                                      id={`tooltip-${transaction._id}`}
                                                                    >
                                                                      <p className="text-white mb-0 fs-14 fw-500">
                                                                        Receipt not uploaded yet
                                                                      </p>
                                                                    </Tooltip>
                                                                  }
                                                                >
                                                                  <span className="d-inline-block">
                                                                    <Button className="uw-btn uw-btn-primary uw-btn-sm"
                                                                      variant="primary"
                                                                      size="sm"
                                                                      disabled
                                                                      style={{
                                                                        pointerEvents:
                                                                          "none",
                                                                      }}
                                                                    >
                                                                      View
                                                                      Receipt
                                                                    </Button>
                                                                  </span>
                                                                </OverlayTrigger>
                                                              )}
                                                            </div>
                                                          </td>
                                                        </tr>
                                                      </React.Fragment>
                                                      );
                                                    }
                                                  )}
                                                </>
                                              ) : (
                                                <tr>
                                                  <td colSpan={9}>
                                                    <div className="w-100 fs-15 fw-bold d-flex justify-content-center">
                                                      No Data found
                                                    </div>
                                                  </td>
                                                </tr>
                                              )}
                                              <tr className="">
                                                <td colSpan={1}>
                                                  Total Amount:
                                                </td>
                                                <td className="text-center">
                                                  TSh{" "}
                                                  {Method.getGeneralizedAmount(
                                                    order.payment.transactions.reduce(
                                                      (total: number, t: any) =>
                                                        total + (t.charge || 0),
                                                      0
                                                    )
                                                  )}
                                                </td>
                                                <td colSpan={3}></td>
                                              </tr>
                                            </>
                                          </tbody>
                                        </table>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </Card.Body>
                  </Card>

                  {/* Pagination */}
                  {!fetchLoader && totalRecords > 0 && (
                    <OrderPagination
                      totalRecords={totalRecords}
                      currentPage={page}
                      handleCurrentPage={handleCurrentPage}
                      handleNextPage={handleNextPage}
                      handlePreviousPage={handlePreviousPage}
                      handlePageLimit={handlePageLimit}
                      pageLimit={pageLimit}
                    />
                  )}
                </>
              ) : (
                <div className="border border-r10px mb-6">
                  <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3">
                    <span className="fs-18 fw-500">No Data Found</span>
                  </div>
                </div>
              )
            ) : (
              <div className="border border-r10px mb-6">
                <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3">
                  <Loader loading={fetchLoader || loading} />
                </div>
              </div>
            )}
          </Col>
        </Row>
      </div>

      {/* Receipt Modal */}
      <Modal
        show={showReceiptModal}
        onHide={() => {
          setShowReceiptModal(false);
          setSelectedTransactionId(undefined);
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedReceipt ? "View Receipt" : "Upload Receipt"}
            {selectedTransactionId && (
              <div className="fs-14 text-muted mt-1">
                Order ID: {selectedOrder.refKey}
              </div>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedReceipt ? (
            <div className="text-center">
              <Image
                src={selectedReceipt}
                alt="Payment Receipt"
                fluid
                className="border border-r10px"
              />
            </div>
          ) : (
            <Form>
              {selectedOrder && (
                <div className="mb-3 p-3 bg-light border border-r10px">
                  <h6 className="fw-bold">Order Information</h6>
                  <div className="row">
                    <div className="col-6">
                      <span className="text-muted">Order ID:</span>
                      <div className="fw-500">{selectedOrder.refKey}</div>
                    </div>
                    <div className="col-6">
                      <span className="text-muted">Customer:</span>
                      <div className="fw-500">
                        {selectedOrder.customer?.name}
                      </div>
                    </div>
                    <div className="col-6">
                      <span className="text-muted">Amount:</span>
                      <div className="fw-500">
                        TSh{" "}
                        {Method.getGeneralizedAmount(
                          selectedOrder.payment?.totalCharge || 0
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <Form.Group className="mb-3">
                <Form.Label>Select Receipt Image</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="form-control-lg"
                />
                <Form.Text className="text-muted">
                  Please upload a clear image of the payment receipt
                </Form.Text>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        {!selectedReceipt && (
          <Modal.Footer>
            <Button className="uw-btn uw-btn-light"
              variant="secondary"
              onClick={() => {
                setShowReceiptModal(false);
                setSelectedTransactionId(undefined);
              }}
            >
              Cancel
            </Button>
            <Button className="uw-btn uw-btn-primary"
              variant="primary"
              onClick={handleUploadSubmit}
              disabled={!selectedFile || uploadingReceipt}
            >
              {uploadingReceipt ? "Uploading..." : "Upload Receipt"}
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* Bank Selection Modal */}
      <Modal
        show={showBankModal}
        onHide={() => setShowBankModal(false)}
        size="sm"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Select Bank</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-column gap-3">
            <div className="d-flex justify-content align-items-center">
              <span className="d-flex justify-content-end align-items-center">
                <span className="symbol symbol-circle symbol-40px me-2">
                  <img
                    src={selectedBank === DTBBank ? blueCheck : unChecked}
                    alt=""
                    onClick={() => setSelectedBank(DTBBank)}
                  />
                </span>
              </span>
              <span className="fs-20 fw-600">DTB Bank</span>
            </div>
            <div className="d-flex justify-content align-items-center">
              <span className="d-flex justify-content-end align-items-center">
                <span className="symbol symbol-circle symbol-40px me-2">
                  <img
                    src={selectedBank === CRDBBank ? blueCheck : unChecked}
                    alt=""
                    onClick={() => setSelectedBank(CRDBBank)}
                  />
                </span>
              </span>
              <span className="fs-20 fw-600">CRDB Bank</span>
            </div>
            <div className="d-flex justify-content align-items-center">
              <span className="d-flex justify-content-end align-items-center">
                <span className="symbol symbol-circle symbol-40px me-2">
                  <img
                    src={selectedBank === NMBBank ? blueCheck : unChecked}
                    alt=""
                    onClick={() => setSelectedBank(NMBBank)}
                  />
                </span>
              </span>
              <span className="fs-20 fw-600">NMB Bank</span>
            </div>
            <div className="d-flex justify-content align-items-center">
              <span className="d-flex justify-content-end align-items-center">
                <span className="symbol symbol-circle symbol-40px me-2">
                  <img
                    src={selectedBank === StanbicBank ? blueCheck : unChecked}
                    alt=""
                    onClick={() => setSelectedBank(StanbicBank)}
                  />
                </span>
              </span>
              <span className="fs-20 fw-600">Stanbic Bank</span>
            </div>
            <div className="d-flex justify-content align-items-center">
              <span className="d-flex justify-content-end align-items-center">
                <span className="symbol symbol-circle symbol-40px me-2">
                  <img
                    src={selectedBank === AirtelMoney ? blueCheck : unChecked}
                    alt=""
                    onClick={() => setSelectedBank(AirtelMoney)}
                  />
                </span>
              </span>
              <span className="fs-20 fw-600">Paid via Airtel Money</span>
            </div>
            <div className="d-flex justify-content align-items-center">
              <span className="d-flex justify-content-end align-items-center">
                <span className="symbol symbol-circle symbol-40px me-2">
                  <img
                    src={selectedBank === HaloPesa ? blueCheck : unChecked}
                    alt=""
                    onClick={() => setSelectedBank(HaloPesa)}
                  />
                </span>
              </span>
              <span className="fs-20 fw-600">Paid via Halo Pesa</span>
            </div>
            <div className="form-check form-check-custom form-check-solid me-6 mb-lg-0">
              <input
                type="text"
                className={clsx(
                  "form-control form-control-custom borde-r8px bg-light w-375px ps-11",
                  !transactionValidation ? "" : "border-danger"
                )}
                name="Search Team"
                onChange={(event: any) => {
                  if (event.target.value.trim().length > 0) {
                    setTransactionValidation(false);
                  } else {
                    setTransactionValidation(true);
                  }
                  setTransactionId(event.target.value.trim());
                }}
                placeholder="Enter Transaction Id"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button className="uw-btn uw-btn-light" variant="secondary" onClick={() => setShowBankModal(false)}>
            Cancel
          </Button>
          <Button className="uw-btn uw-btn-primary"
            variant="primary"
            onClick={() => handleBankSelection(selectedBank)}
            disabled={verified}
          >
            {verified ? (
              <span className="indicator-progress" style={{ display: "block" }}>
                Please wait...
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            ) : (
              <span className="indicator-label">Submit</span>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showTigoModal}
        onHide={() => {
          setShowTigoModal(false);
          setTigoReferenceValidation(false);
          setTigoReceiptValidation(false);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Verify Tigo Pesa Payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-4">
              <Form.Label>Tigo Reference No</Form.Label>
              <Form.Control
                type="text"
                value={tigoReferenceNo}
                onChange={(event: any) => {
                  const value = event.target.value.replace(/[^a-zA-Z0-9]/g, "");
                  setTigoReferenceNo(value);
                  setTigoReferenceValidation(!value.trim());
                }}
                className={clsx(
                  "form-control-lg",
                  tigoReferenceValidation ? "border-danger" : ""
                )}
                placeholder="Enter Tigo reference no"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Receipt Image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleTigoReceiptChange}
                className={clsx(
                  "form-control-lg",
                  tigoReceiptValidation ? "border-danger" : ""
                )}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button className="uw-btn uw-btn-light"
            variant="secondary"
            onClick={() => {
              setShowTigoModal(false);
              setTigoReferenceValidation(false);
              setTigoReceiptValidation(false);
            }}
          >
            Cancel
          </Button>
          <Button className="uw-btn uw-btn-primary"
            variant="primary"
            onClick={handleTigoSubmit}
            disabled={tigoSubmitting}
          >
            {tigoSubmitting ? (
              <span className="indicator-progress" style={{ display: "block" }}>
                Please wait...
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            ) : (
              <span className="indicator-label">Submit</span>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default OutstandingReportComponent;
